/**
 * Tauri-specific auto-connect for PowerSync.
 *
 * `@powersync/tauri-plugin@0.0.4` does not allow calling `db.connect()` from
 * JavaScript — the sync loop must be started by the Rust crate. This module
 * mirrors `handlePowerSyncAutoConnect` (the JS web path) but
 * `invoke()`s the `powersync_connect` Tauri command we expose in
 * `apps/desktop/src-tauri/src/lib.rs` instead.
 *
 * Why JS fetches the JWT (and passes it down) instead of Rust:
 * the WebView origin (`tauri.localhost`) and the backend (`localhost:3333`)
 * are cross-origin, so the session cookie that Better Auth sets on the
 * backend origin is invisible to `document.cookie`. JS *can* still reach
 * the backend via `fetch` with `credentials: "include"` (the WebView's
 * cookie jar honours the cookie's domain scope), so we do the credentials
 * exchange here, then hand the resulting JWT to Rust.
 *
 * Token refresh: a `setInterval` re-fetches the JWT before it expires and
 * pushes the new value via the `powersync_update_token` Tauri command.
 * The Rust connector reads the latest token on every `fetch_credentials`,
 * so the sync loop survives long sessions without restarting.
 */

import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { ApiClient } from "@custodia/client";
import { readLegacyToken } from "@/lib/secure-token";

/**
 * Reads the `rustHandle` getter that `PowerSyncTauriDatabase` adds on top of
 * `AbstractPowerSyncDatabase`. Uses the `in` operator + `typeof` to narrow
 * the value at runtime without any unsafe assertion.
 */
function getRustHandle(database: AbstractPowerSyncDatabase): number | null {
  if (!("rustHandle" in database)) {
    return null;
  }
  const handle = database.rustHandle;
  return typeof handle === "number" ? handle : null;
}

function readSessionToken(): string | null {
  // Phase 3: the Better-Auth session token's durable copy lives in the OS
  // keychain (desktop); `readLegacyToken` returns it synchronously from the
  // in-memory cache (falling back to localStorage on mobile / pre-hydration).
  return readLegacyToken();
}

function readDocumentCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  return document.cookie || null;
}

/**
 * Refresh the JWT a comfortable margin before it expires. PowerSync tokens
 * are typically short-lived (~5–15 min) and the SDK's own refresh logic is
 * bypassed in Tauri because the connector lives in Rust.
 */
const TOKEN_REFRESH_INTERVAL_MS = 60_000;

let tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;

async function fetchJwt(api: ApiClient): Promise<string | null> {
  try {
    const { data, error, response } = await api.GET("/api/auth/get-session", {
      params: { query: { jwt: true } },
    });
    if (!response.ok || !data) {
      console.warn("[PowerSync/Tauri] /api/auth/get-session failed:", error ?? response.status);
      return null;
    }
    return data.token ?? null;
  } catch (err) {
    console.warn("[PowerSync/Tauri] /api/auth/get-session threw:", err);
    return null;
  }
}

function stopTokenRefresh(): void {
  if (tokenRefreshTimer !== null) {
    clearInterval(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
}

interface TauriConnectOptions {
  database: AbstractPowerSyncDatabase;
  /** Backend API client, used to fetch / refresh the PowerSync JWT.*/
  apiClient: ApiClient;
  /** Base URL of the Custodia API (e.g. `http://localhost:3333`).*/
  apiBase: string;
  /** PowerSync service endpoint (e.g. `http://localhost:8085`).*/
  powerSyncEndpoint: string;
  isAuthenticated: boolean;
  isVisitor?: boolean;
  isPriming?: boolean;
  isConnecting: boolean;
  setIsConnecting: (val: boolean) => void;
  onStatusUpdate?: (update: { isConnected: boolean }) => void;
  /**
   * Server maintenance window with a `sync`/`all` scope. On Tauri the sync loop
   * lives in Rust, so pausing means telling Rust to disconnect — the JS upload
   * gate (`canUploadNow`) is not the upload path here. See
   * `AutoConnectOptions.isMaintenancePaused`.
   */
  isMaintenancePaused?: boolean;
}

export async function handleTauriPowerSyncAutoConnect(options: TauriConnectOptions): Promise<void> {
  const {
    database,
    apiClient,
    apiBase,
    powerSyncEndpoint,
    isAuthenticated,
    isVisitor = false,
    isPriming = false,
    isConnecting,
    setIsConnecting,
    onStatusUpdate,
    isMaintenancePaused = false,
  } = options;

  if (isPriming) {
    return;
  }

  const handle = getRustHandle(database);
  if (handle === null) {
    console.warn(
      "[PowerSync/Tauri] Database has no `rustHandle` — is `PowerSyncTauriDatabase` initialised? Skipping connect.",
    );
    return;
  }

  const { invoke } = await import("@tauri-apps/api/core");

  if (isMaintenancePaused) {
    if (database.connected || isConnecting) {
      console.warn("[PowerSync/Tauri] Server maintenance window. Disconnecting until it lifts.");
      setIsConnecting(false);
      stopTokenRefresh();
      try {
        await invoke("powersync_disconnect", { handle });
        onStatusUpdate?.({ isConnected: false });
      } catch (err) {
        console.error("[PowerSync/Tauri] Disconnection failed:", err);
      }
    }
    return;
  }

  if (isVisitor || !isAuthenticated) {
    if (database.connected || isConnecting) {
      console.warn("[PowerSync/Tauri] Visitor or no auth detected. Forcing disconnection.");
      setIsConnecting(false);
      stopTokenRefresh();
      try {
        await invoke("powersync_disconnect", { handle });
        onStatusUpdate?.({ isConnected: false });
      } catch (err) {
        console.error("[PowerSync/Tauri] Disconnection failed:", err);
      }
    }
    return;
  }

  if (!database.connected && !isConnecting) {
    console.log("[PowerSync/Tauri] Auto-connecting via Rust...");
    setIsConnecting(true);
    try {
      const token = await fetchJwt(apiClient);
      if (!token) {
        console.warn("[PowerSync/Tauri] No JWT available — skipping connect");
        return;
      }
      // Pull the Better-Auth session token from `localStorage` (written by
      // `auth.ts` after sign-in). Rust uses this — not the PowerSync JWT —
      // to authenticate CRUD upload requests to `/api/sync/batch`.
      await invoke("powersync_connect", {
        handle,
        apiBase,
        powersyncEndpoint: powerSyncEndpoint,
        token,
        sessionToken: readSessionToken(),
        sessionCookie: readDocumentCookie(),
      });
      onStatusUpdate?.({ isConnected: true });

      // Keep the Rust-side token fresh. The connector reads the value on
      // every `fetch_credentials` call, so an interval push is enough.
      stopTokenRefresh();
      tokenRefreshTimer = setInterval(() => {
        void refreshTauriTokens(apiClient);
      }, TOKEN_REFRESH_INTERVAL_MS);
    } catch (err) {
      console.error("[PowerSync/Tauri] Auto-connection failed:", err);
    } finally {
      setIsConnecting(false);
    }
  }
}

/**
 * Periodic token-refresh task driven by `setInterval`. Pushes the latest
 * PowerSync JWT *and* Better-Auth session token down to Rust so the sync
 * loop survives indefinitely without a reconnect.
 */
async function refreshTauriTokens(apiClient: ApiClient): Promise<void> {
  const { invoke } = await import("@tauri-apps/api/core");

  const next = await fetchJwt(apiClient);
  if (next !== null) {
    try {
      await invoke("powersync_update_token", { token: next });
    } catch (err) {
      console.warn("[PowerSync/Tauri] update_token failed:", err);
    }
  }

  // Always push the current session token too — cheap and resilient if the
  // user rotated the session (e.g. password reset) without restarting the
  // sync loop.
  try {
    await invoke("powersync_update_session_token", {
      sessionToken: readSessionToken(),
    });
  } catch (err) {
    console.warn("[PowerSync/Tauri] update_session_token failed:", err);
  }
}

export async function tauriDisconnect(database: AbstractPowerSyncDatabase): Promise<void> {
  stopTokenRefresh();
  const handle = getRustHandle(database);
  if (handle === null) {
    return;
  }
  const { invoke } = await import("@tauri-apps/api/core");
  try {
    await invoke("powersync_disconnect", { handle });
  } catch (err) {
    console.warn("[PowerSync/Tauri] disconnect failed:", err);
  }
}
